import React, { useEffect, useState } from "react";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { useAuth } from "../../../context/AuthProvider";
import { ErrorCircleIcon, SubdirectoryRightIcon } from "../../../constants/Icons";

function SaleControl() {
  const { checkNetwork } = useNetwork();
  const { updateSettings, storeSettings } = useAuth();
  const showAlert = useShowAlert();

  const [loading, setLoading] = useState({});
  const [toggles, setToggles] = useState({});

  const toggleItems = [
    // --- Sale Settings ---
    {
      section: "saleSettings",
      key: "customer",
      label: "Customer Mandatory",
      icon: "ng ng-ng-customers-add-icon",
      settingLable: "isCustomerMandatory",
      color: "gray",
      subtext:
        "When enabled, you must select or add a customer during the sale. If disabled, adding customer details is optional.",
    },
    {
      section: "saleSettings",
      key: "saleprice",
      label: "Modify Sale Price",
      icon: "bx bx-purchase-tag",
      settingLable: "modifySalePrice",
      color: "gray",
      subtext: "When enabled, you can modify the sale price.",
    },
    {
      section: "saleSettings",
      key: "barcode",
      label: "Barcode Scan",
      icon: "bx bx-barcode",
      settingLable: "isScanBarcode",
      color: "gray",
      subtext: "When enabled, you can Search the product by barcode.",
    },
    {
      section: "saleSettings",
      key: "isOffer",
      label: "Offer",
      icon: "bx bxs-offer",
      settingLable: "isOffer",
      color: "gray",
      subtext: "When enabled, you can set the offer for sale.",
    },
    {
      section: "saleSettings",
      key: "isRemark",
      label: "Remark",
      icon: "bx bx-notepad",
      settingLable: "isRemark",
      color: "gray",
      subtext: "When enabled, you can set the Remark for sale.",
    },
    {
      section: "saleSettings",
      key: "isConvertToInvoice",
      label: "Convert to Invoice",
      icon: "bx bx-receipt",
      settingLable: "isConvertToInvoice",
      color: "gray",
      subtext: "When enabled, you can convert the sale to an invoice.",
    },
    {
      section: "saleSettings",
      key: "isOverallDiscount",
      label: "Overall Discount",
      icon: "bx bxs-discount",
      settingLable: "isOverallDiscount",
      color: "gray",
      subtext: "When enabled, you can add overall Discount to a sale.",
    },
    {
      section: "saleSettings",
      key: "isLandingCost",
      label: "Landing Cost Display",
      icon: "bx bx-calculator",
      settingLable: "isLandingCost",
      color: "gray",
      subtext:
        "Enable this option to display the Landing cost in Sale section.",
    },
    {
      section: "saleSettings",
      key: "isGroupedStock",
      label: "List Stock as Grouped",
      icon: "ng ng-ng-stock-icon",
      settingLable: "isGroupedStock",
      color: "gray",
      subtext: "When enabled, you can select the product like groupedStock .",
    },
    // --- Sale Mode Settings ---
    {
      section: "saleModeSettings",
      key: "bill",
      label: "Bill",
      icon: "bx bx-note",
      settingLable: "isBill",
      color: "gray",
      subtext:
        "When enabled, you can choose the Bill mode for printing or downloading.",
    },
    {
      section: "saleModeSettings",
      key: "billTaxInfo",
      parentKey: "bill",
      label: "Show Tax Information",
      icon: "bx bx-calculator",
      settingLable: "isBill_isTax",
      color: "gray",
      subtext:
        "When enabled, tax details will be displayed in Bill mode while printing, downloading, or sharing.",
    },
    {
      section: "saleModeSettings",
      key: "invoice",
      label: "Invoice",
      icon: "bx bx-notepad",
      settingLable: "isInvoice",
      color: "gray",
      subtext:
        "When enabled, you can choose the Invoice mode for printing or downloading.",
    },
  ];

  //  Fetch existing settings
  const handleList = async () => {
    try {
      if (!checkNetwork()) return;

      const response = await AxiosInstance.get("module/storesettings");
      const saleSettings = response.data?.results?.saleSettings || {};
      const saleModeSettings = saleSettings?.saleModeSettings || {};

      const updatedToggles = {};

      toggleItems.forEach((item) => {
        if (item.section === "saleModeSettings") {
          updatedToggles[item.key] =
            saleModeSettings[item.settingLable] ?? false;
        } else {
          updatedToggles[item.key] = saleSettings[item.settingLable] ?? false;
        }
      });

      setToggles(updatedToggles);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  //  Update specific toggle
  const handleUpdate = async (
    value,
    key,
    settingLable,
    section,
    extraUpdate = {},
  ) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      if (!checkNetwork()) return;

      let data = {
        saleSettings: {},
      };

      if (section === "saleModeSettings") {
        data.saleSettings.saleModeSettings = {
          [settingLable]: value,
          ...extraUpdate,
        };
      } else {
        data.saleSettings[settingLable] = value;
        Object.assign(data.saleSettings, extraUpdate);
      }

      const response = await AxiosInstance.patch(`module/storesettings`, data);
      const savedSettings = response.data?.results?.saleSettings || {};
      const targetScope =
        section === "saleModeSettings"
          ? savedSettings.saleModeSettings || {}
          : savedSettings;
      if (Object.prototype.hasOwnProperty.call(targetScope, settingLable)) {
        showAlert("success", response?.data?.message);

        //  Update local and context states
        setToggles((prev) => ({
          ...prev,
          [key]: value,
          ...Object.entries(extraUpdate).reduce((acc, [extraKey, extraVal]) => {
            const match = toggleItems.find((i) => i.settingLable === extraKey);
            if (match) acc[match.key] = extraVal;
            return acc;
          }, {}),
        }));

        updateSettings({
          storeSettings: {
            ...storeSettings,
            saleSettings: {
              ...storeSettings?.saleSettings,
              ...(section === "saleModeSettings"
                ? {
                    saleModeSettings: {
                      ...storeSettings?.saleSettings?.saleModeSettings,
                      [settingLable]: value,
                      ...extraUpdate,
                    },
                  }
                : {
                    [settingLable]: value,
                    ...extraUpdate,
                  }),
            },
          },
        });
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Toggle handler
  const handleSwitchChange = (event, key, settingLable, section) => {
    const newValue = event.target.checked;

    if (section === "saleModeSettings") {
      // Prevent both being OFF
      if (key === "bill" && !newValue) {
        handleUpdate(false, key, settingLable, section, { isInvoice: true });
        return;
      }
      //  if (key === "billTaxInfo" && !newValue) {
      //   handleUpdate(false, key, settingLable, section, { isTaxInfo: true });
      //   return;
      // }
      if (key === "invoice" && !newValue) {
        handleUpdate(false, key, settingLable, section, { isBill: true });
        return;
      }
    }

    handleUpdate(newValue, key, settingLable, section);
  };

  useEffect(() => {
    handleList();
  }, []);

  // Group toggles by section
  const groupedSettings = [
    {
      name: "Sales & Pre Sale Settings",
      items: toggleItems.filter((i) => i.section === "saleSettings"),
    },
    {
      name: "Sales Mode Settings",
      items: toggleItems.filter((i) => i.section === "saleModeSettings"),
    },
  ];

  return (
    <div className="row">
      <div className="col-lg-5">
        {groupedSettings.map((group) => (
          <div className="card mb-3" key={group.name}>
            <div className="card-header p-3 bg-light-core">
              <h6 className="mb-0 text-dark">{group.name}</h6>
            </div>
            <div className="card-body">
              {group.items
                .filter((item) => item.key !== "billTaxInfo")
                .map(
                  ({
                    key,
                    label,
                    icon,
                    color,
                    settingLable,
                    subtext,
                    section,
                  }) => (
                    <>
                      <div key={key} className="d-flex align-items-center">
                        <i
                          className={`${icon} font-27`}
                          style={{ color: toggles[key] ? color : "gray" }}
                        />
                        <div className="flex-grow-1 ms-2">
                          <label className="mb-0">
                            {label}
                            <span className="tooltip-container ms-1">
                              <span className="cursor-poInter">
                                <ErrorCircleIcon/>{" "}
                              </span>
                              <span className="tooltip-text">{subtext}</span>
                            </span>
                          </label>
                        </div>
                        <div className="ms-auto custom-toggle-wrapper">
                          <label className="custom-toggle">
                            <input
                              type="checkbox"
                              checked={toggles[key] || false}
                              onChange={(e) =>
                                handleSwitchChange(
                                  e,
                                  key,
                                  settingLable,
                                  section,
                                )
                              }
                              disabled={loading[key]}
                            />
                            <span className="slider cursor-poInter">
                              {loading[key] ? (
                                <span
                                  className={`spinner-inside ${
                                    toggles[key] ? "spinner-right" : ""
                                  }`}
                                />
                              ) : (
                                <>
                                  {toggles[key] ? (
                                    <span className="label-on ms-0-1">ON</span>
                                  ) : (
                                    <span className="label-off ms-3">OFF</span>
                                  )}
                                </>
                              )}
                            </span>
                          </label>
                        </div>
                      </div>
                      {key === "bill" && toggles.bill && (
                        <div
                          className="d-flex align-items-center ms-4 mt-2 mb-2"
                          style={{ paddingLeft: "20px" }}
                        >
                          <SubdirectoryRightIcon iClass={"me-2 text-muted"}/>

                          <div className="flex-grow-1">
                            <label className="mb-0">Show Tax Information</label>
                          </div>

                          <div className="ms-auto custom-toggle-wrapper">
                            <label className="custom-toggle">
                              <input
                                type="checkbox"
                                checked={toggles.billTaxInfo || false}
                                onChange={(e) =>
                                  handleSwitchChange(
                                    e,
                                    "billTaxInfo",
                                    "isBill_isTax",
                                    "saleModeSettings",
                                  )
                                }
                                disabled={loading.billTaxInfo}
                              />

                              <span className="slider cursor-poInter">
                                {toggles.billTaxInfo ? (
                                  <span className="label-on ms-0-1">ON</span>
                                ) : (
                                  <span className="label-off ms-3">OFF</span>
                                )}
                              </span>
                            </label>
                          </div>
                        </div>
                      )}
                    </>
                  ),
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SaleControl;
